/**
 * @author Charles Blais <charles.blais@canada.ca>
 * 
 * EChart libraries properties
 */

import { LanguageState } from "../../contexts/language-state-provider";

//const xIcon = 'path://M 0 66 L 23.5 35.3 L 1 5.9 L 8.9 0 L 29.2 28.4 L 49.3 0.3 L 56.7 5.9 L 34.9 34.8 L 58.7 66 L 51 71.9 L 28.9 41.9 L 7.5 71.7 L 0 66 Z';
//const yIcon = 'path://M 8 0 L 29.4 35.5 L 50.4 0.4 L 57.9 4.8 L 33.8 44.3 L 33.8 70.9 L 24.3 70.9 L 24.3 44.3 L 0 4.8 L 8 0 Z';
export const lIcon = 'path://M 43.8 22 L 43.8 30.7 Q 46.6 26.5 50.95 23.65 A 17.316 17.316 0 0 1 58.056 21.025 A 22.629 22.629 0 0 1 61.3 20.8 A 23.225 23.225 0 0 1 66.365 21.318 Q 71.459 22.456 74.45 26.1 Q 78.575 31.126 78.788 39.209 A 33.764 33.764 0 0 1 78.8 40.1 L 78.8 74 L 69.8 74 L 69.8 41.1 Q 69.8 36.744 68.282 33.761 A 10.688 10.688 0 0 0 67.2 32.05 A 8.461 8.461 0 0 0 61.255 28.783 A 12.388 12.388 0 0 0 59.8 28.7 Q 57.5 28.7 55.3 29.55 A 18.175 18.175 0 0 0 51.597 31.513 A 20.625 20.625 0 0 0 51.05 31.9 Q 49 33.4 47.25 35.4 Q 45.5 37.4 44.1 39.6 L 44.1 74 L 35.1 74 L 35.1 22 L 43.8 22 Z M 9 0 L 9 56.8 A 20.644 20.644 0 0 0 9.188 59.688 Q 9.65 62.952 11.25 64.8 A 7.363 7.363 0 0 0 15.834 67.276 A 10.724 10.724 0 0 0 17.5 67.4 Q 19.4 67.4 21.2 66.95 Q 23 66.5 24.4 65.9 L 26.6 73.2 Q 25.201 73.822 23.288 74.323 A 34.978 34.978 0 0 1 22.15 74.6 A 23.816 23.816 0 0 1 18.995 75.085 A 30.514 30.514 0 0 1 16.3 75.2 A 21.419 21.419 0 0 1 12.189 74.819 A 18.058 18.058 0 0 1 9.85 74.2 Q 6.9 73.2 4.7 71.1 A 13.281 13.281 0 0 1 2.222 67.827 A 16.994 16.994 0 0 1 1.25 65.7 Q 0 62.4 0 57.7 L 0 0 L 9 0 Z';
//export const markerIcon = 'path://M 0 24.1 L 9.25 13.35 L 0.35 3.05 L 3.85 0 L 11.9 10.4 L 19.8 0.2 L 23.15 3.05 L 14.5 13.15 L 24 24.1 L 20.6 27 L 11.85 16.15 L 3.2 26.9 L 0 24.1 Z';
export const markerIcon = 'path://M 24 9 a 16 16 0 1 0 2 0 z m 9 8 l -16 16 m 0 -16 l 16 16';

/**
 * Generate and echart subtext using the variables
 * 
 * We are assuming coordinates are always North West
 */
 export const getSubtext = (
  variables: HazardParameters,
  log: boolean,
  lang: LanguageState
): string => {
  const { latitude, longitude, code } = variables;
  const displayLat = Math.round(latitude * 1000) / 1000;
  const displayLon = -Math.round(longitude * 1000) / 1000;

  const model = ( /^nbc/i.test(code) ) ? lang.codeMap[code] : lang.modelMap[code];

  let west = 'W';
  let logText = (log) ? 'log-log interpolation' : 'linear-linear interpolation';
  if( lang.abbr === 'fr' ) {
    west = 'O';
    logText = (log) ? 'Interpolation bi-logarithmique' : 'Interpolation bilinéaire';
  }
  return `${displayLat}N ${displayLon}${west}, ${model}, ${logText}`
}


/**
 * Get toolbox
 * @param {*} lang 
 * @param {*} log 
 * @param {*} setLog 
 */
export const getToolbox = (
  lang: LanguageState,
  log: boolean,
  setLog: (v: boolean) => void,
) => {
  return {
    left: 'center',
    feature: {
      saveAsImage: {
        title: lang.chart.downloadImage,
      },
      restore: {
        title: lang.chart.restore,
      },
      myLog: {
        show: true,
        title: log ? 'Linear': 'Log', 
        icon: lIcon,
        iconStyle: {
          borderColor: log ? 'blue' : 'gray',
        },
        onclick: () => setLog(!log),
      }
    }
  }
};

/**
 * Get X axis properties
 * @param {*} name 
 * @param {*} xlog 
 */
export const getXAxis = (
  name: string,
  xlog: boolean
): any => {
  return {
    type: xlog ? 'log' : 'value',
    boundaryGap: false,
    name: name,
    nameLocation: 'middle',
    nameGap: 25,
    nameTextStyle: {
      fontSize: 16,
    },
    minorTick: {
      show: true,
      splitNumber: 9,
    },
    minorSplitLine: {
      show: true,
    },
    min: (v: any) => v.min*0.98,
    max: (v: any) => v.max*1.02,
    axisLabel: {
      showMinLabel: false,
      showMaxLabel: false,
      fontSize: 14,
    }
  };
}

/**
 * Get Y axis properties
 * @param {*} name 
 * @param {*} ylog 
 */
export const getYAxis = (
  name: string,
  ylog: boolean
): any => {
  return {
    type: ylog ? 'log' : 'value',
    name: name,
    nameLocation: 'center',
    nameGap: 50,
    nameTextStyle: {
      align: 'center',
      fontSize: 16,
    },
    minorTick: {
      show: true,
      splitNumber: 9,
    },
    minorSplitLine: {
        show: true
    },
    min: (v: any) => v.min*0.98,
    max: (v: any) => v.max*1.02,
    axisLabel: {
      showMinLabel: false,
      showMaxLabel: false,
      fontSize: 14,
    }
  };
};

/**
 * Create selected legend items
 * @param {*} series 
 * @param {*} selected 
 */
export const getLegend = (
  series: any[],
  selected: string[],
  exactMatch: boolean = true
): any => {
  return {
    type: 'scroll',
    orient: 'vertical',
    right: 10,
    top: 20,
    bottom: 20,
    data: series.map((serie) => serie.name),
    textStyle: {
      fontSize: 14,
    },
    selected: series.reduce((ac,a) => (
      {
        ...ac,
        [a.name]: (exactMatch)
          ? selected.indexOf(a.name) >= 0
          : selected.reduce((sc, s) => (!sc)?a.name.includes(s):sc, false)
      }
    ), {})
  };
};


export const getOnEvents = (
  setSelected: (v: string[]) => void
): any => {
  return {
    legendselectchanged: (e: any) => {
      setSelected(Object.keys(e.selected).reduce<string[]>((acc, key) => {
        if ( e.selected[key] ) acc.push(key);
        return acc;
      }, []));
    }
  }
};

/**
 * Get tooltip
 */
export const getTooltip = (
  sig: number = 3
): any => {

  const formatter = ({value}: {value: string}) => {
    return Number(value).toPrecision(sig)
  }

  return {
    trigger: 'none',
    axisPointer: {
      type: 'cross',
      label: {
        formatter,
      }
    } 
  };
};

/**
 * Get grid
 */
export const getGrid = (): any => {
  return {
    left: 40,
    right: 150,
    bottom: '18%',
    containLabel: true,
  };
};


export const getDataZoom = (): any => {
  return [{
    type: 'inside',
    filterMode: 'none',
    zoomLock: true,
    zoomOnMouseWheel: false,
    moveOnMouseMove: false,
  }, {
    type: 'slider',
    bottom: 40
  }];
}

/**
 * Convert sa string to numeric value
 */
export const saToNumber = (key: string): number => {
  return Number(key.substring(2).replace('p', '.'))
}

/**
 * Convert the SA units to a echart series
 */
export const saToSeries = (siteDesignation: SiteDesignation): SeriesData => {
  return Object.keys(siteDesignation).reduce<SeriesData>((acc, key) => {
    if(key.startsWith('sa')){
      const value = saToNumber(key);
      // remove any value <= 0.001 if Sa is <= 2, else remove 0.0001
      // @ts-ignore: we know the key will exist
      if(value <= 2.0 && siteDesignation[key] <= 0.001) return acc;
      // @ts-ignore: we know the key will exist
      else if(value > 2.0 && siteDesignation[key] <= 0.0001) return acc;
      // @ts-ignore: we know the key will exist
      acc.push([value, siteDesignation[key]])
    }
    return acc;
  }, []);
}


/**
 * remove any value <= 0.001 if Sa is <= 2, else remove 0.0001
 */
export const hideSaFromSiteDesignations = (selectedSa: string, siteDesignations: SiteDesignation[]): SiteDesignation[] => {
  const value = saToNumber(selectedSa);
  return siteDesignations.filter(site => {
    // @ts-ignore: we know the key will exist
    if(value <= 2.0 && site[selectedSa] <= 0.001) return false;
    // @ts-ignore: we know the key will exist
    else if(value > 2.0 && site[selectedSa] <= 0.0001) return false;
    return true;
  });
}

/**
 * remove any values where pga <= 0.001
 */
 export const hidePGAFromSiteDesignations = (siteDesignations: SiteDesignation[]): SiteDesignation[] => {
  return siteDesignations.filter(site => {
    return site.pga > 0.001
  });
}


/**
 * Set color to series.  We loop through the series and anything with:
 * 
 *  itemStyle
 *  lineStyle
 * 
 * Are added properties:
 * 
 *  borderColor
 *  color
 * 
 * respectively based on their index in the colorTable.
 * 
 */
export const modifyColorToSeries = (series: any[], colorTable: string[]) => {

  return series.map((serie, idx) => {
    const color = colorTable[idx % colorTable.length];
    if( 'itemStyle' in serie ) {
      serie.itemStyle.borderColor = color;
    }
    if( 'lineStyle' in serie ) {
      serie.lineStyle.color = color;
    }
    if( 'markPoint' in serie && 'itemStyle' in serie.markPoint ) {
      serie.markPoint.itemStyle.borderColor = color;
    }
    return serie;
  })

}